import moment from 'moment'
import { isEmpty } from 'lodash'

import { fetchingAPI, apiService } from '@/api'
import { toast } from '@/components/toasts/toast.helpers'

import * as redux from './commandCenter.redux'
import {
  getAgentAndTagParams,
  getMergedSentimentData,
  appendSentimentFilterParams,
} from './commandCenter.helpers'
import { INBOX_FILTER_TYPE, SENTIMENT_FILTER_TYPE } from '../savedFilters/savedFilters.constants'
import { fetchSavedFilters } from '../savedFilters/savedFilters.actions'

export const fetchInitialFilters = (filterType) => async (dispatch, getState) => {
  await dispatch(fetchSavedFilters(filterType))
  const filters = getState().savedFilters.savedFilterList[filterType]

  // Find the default saved filter, if exists
  const defaultFilter = filters.find((filter) => filter.is_default)

  if (defaultFilter) {
    dispatch(redux.setSavedFilterId(defaultFilter.uuid))
  }
}

export const fetchAgentsAudioIssues = async (dispatch, organizationId, filterToUse) => {
  const filterParams = filterToUse
    ? getAgentAndTagParams(filterToUse, 'agent_ids', 'tag_ids')
    : null
  const filters = filterParams ? `?${filterParams.toString()}` : ''

  const url = `${apiService.reporting}/api/calls/user_call_issues/${organizationId}${filters}`
  try {
    const response = await fetchingAPI(url, 'GET', dispatch, undefined, undefined, {
      isSilent: true,
    })
    dispatch(redux.setAgentsAudioIssues(response))
  } catch (err) {
    console.error(err)
  }
}

/**
 * @param {Object} options
 * @param {boolean} options.silent Swallow a failure instead of toasting it. The
 *   session-wide socket provider prefetches this roster on every page, so a
 *   transient failure would otherwise raise a red toast over whatever the user
 *   is actually doing — and the toast blocks clicks underneath it.
 */
export const fetchAgentsWithCallStatus =
  ({ silent = false } = {}) =>
  async (dispatch, getState) => {
    const organizationId = getState().currentUser.organizationid
    const ownOrganizationId = getState().currentUser.own_organization_id
    const childOrgSelected = organizationId !== ownOrganizationId
    const { savedFilterList } = getState().savedFilters
    const { selectedSavedFilterId } = getState().commandCenter
    const inboxSavedFilters = savedFilterList[INBOX_FILTER_TYPE]
    const currentlyAppliedFilter = inboxSavedFilters?.find(
      (filter) => filter.uuid === selectedSavedFilterId
    )

    // for org hierarchy, we disable my team filter when impersonating child organization
    const filterToUse = !childOrgSelected && currentlyAppliedFilter ? currentlyAppliedFilter : null

    dispatch(redux.setLoading({ agents: true }))

    try {
      const startDate = moment().startOf('day').utc().format()
      const filterParams = filterToUse ? getAgentAndTagParams(filterToUse) : null

      const url = filterParams
        ? `${
            apiService.rtc
          }/command-center/${organizationId}/agents?${filterParams.toString()}&startDate=${startDate}`
        : `${apiService.rtc}/command-center/${organizationId}/agents?startDate=${startDate}`

      const response = await fetchingAPI(url, 'GET', dispatch)
      dispatch(redux.setAgents(response))
      fetchAgentsAudioIssues(dispatch, organizationId, filterToUse)
    } catch (err) {
      if (!silent) {
        toast.error('Failed to fetch agents', { cause: err })
      }
    } finally {
      dispatch(redux.setLoading({ agents: false }))
    }
  }

export const fetchAgentWithCallStatus = (agentId) => async (dispatch, getState) => {
  const organizationId = getState()?.currentUser?.organizationid
  const startDate = moment().startOf('day').utc().format()

  try {
    const response = await fetchingAPI(
      `${apiService.rtc}/command-center/${organizationId}/agents/${agentId}?startDate=${startDate}`,
      'GET',
      dispatch
    )
    dispatch(redux.setAgent({ agentId, agentData: response }))
  } catch (err) {
    // Going to avoid a toast here because if something goes wrong, the toasts will be never ending
    console.error(err)
  }
}

export const fetchSentimentData = () => async (dispatch, getState) => {
  const organizationId = getState().currentUser.organizationid
  const ownOrganizationId = getState().currentUser.own_organization_id
  const childOrgSelected = organizationId !== ownOrganizationId
  const { savedFilterList } = getState().savedFilters
  const { selectedSavedFilterId } = getState().commandCenter
  const sentimentSavedFilters = savedFilterList[SENTIMENT_FILTER_TYPE]
  const inboxSavedFilters = savedFilterList[INBOX_FILTER_TYPE]
  const currentlyAppliedFilter = inboxSavedFilters?.find(
    (filter) => filter.uuid === selectedSavedFilterId
  )
  const sentimentConfig = !isEmpty(sentimentSavedFilters) ? sentimentSavedFilters[0] : {}
  // for org hierarchy, we disable my team filter when impersonating child organization
  const filterToUse = !childOrgSelected && currentlyAppliedFilter ? currentlyAppliedFilter : {}
  const inboxFilterParams = getAgentAndTagParams(filterToUse)
  const filterParams = !isEmpty(sentimentConfig.filters)
    ? appendSentimentFilterParams(inboxFilterParams, sentimentConfig)
    : inboxFilterParams

  dispatch(redux.setLoading({ sentiment: true }))

  let sentimentData

  try {
    sentimentData = await fetchingAPI(
      `${apiService.reporting}/api/sentiment?${filterParams.toString()}`,
      'GET',
      dispatch
    )

    dispatch(redux.setSentimentData(sentimentData))
  } catch (err) {
    toast.error('Failed to fetch sentiment data', { cause: err })
  } finally {
    dispatch(redux.setLoading({ sentiment: false }))
  }

  // only re-request calls w/o an upset reason or happy reason
  const upsetCallIds = sentimentData?.bottom_calls
    .filter((data) => !data.upset_reason)
    .map((data) => data.call_id)

  // Don't send an empty envelope in the mail
  if (!isEmpty(upsetCallIds)) {
    dispatch(redux.setLoading({ sentimentUpsetReasons: true }))

    try {
      const additionalUpsetReasons = await fetchingAPI(
        `${apiService.summary}/sentiment/upset_reason`,
        'POST',
        dispatch,
        JSON.stringify({ call_ids: upsetCallIds })
      )
      const mergedSentimentData = getMergedSentimentData(sentimentData, additionalUpsetReasons)
      dispatch(redux.setSentimentData(mergedSentimentData))
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(redux.setLoading({ sentimentUpsetReasons: false }))
    }
  }

  const positiveCallIds = sentimentData?.top_calls
    .filter((data) => !data.positive_reason)
    .map((data) => data.call_id)

  // Don't send an empty envelope in the mail
  if (!isEmpty(positiveCallIds)) {
    dispatch(redux.setLoading({ sentimentPositiveReasons: true }))

    try {
      const additionalHappyReasons = await fetchingAPI(
        `${apiService.summary}/sentiment/positive_reason`,
        'POST',
        dispatch,
        JSON.stringify({ call_ids: positiveCallIds })
      )
      const mergedSentimentData = getMergedSentimentData(sentimentData, additionalHappyReasons)
      dispatch(redux.setSentimentData(mergedSentimentData))
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(redux.setLoading({ sentimentPositiveReasons: false }))
    }
  }
}

export const setSavedFilterIdAndChangeSubscription = (filterUuid) => async (dispatch) => {
  dispatch(redux.setSavedFilterId(filterUuid))
  // If no filter uuid is passed, it will default to 'all', which will fetch events for all users
  dispatch(redux.changeSubscriptionCCSocket({ filter_uuid: filterUuid || 'all' }))
}
